/**
 * @file React компонент для Field1 Canvas с WebGL
 * @module membrane/src/visual/field1/Field1Canvas
 * 
 * Рендерит очень светлое, почти белое поле туманности через WebGL fragment shader.
 */

import { useEffect, useRef, useState } from 'react';
import { Field1Params, paramsToUniforms, DEFAULT_FIELD1_PARAMS } from './field1Params';
import { Field1Controller } from './field1Controller';

// Shader источники (встроенные строки)
const vertexShaderSource = `
attribute vec2 a_position;

void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const fragmentShaderSource = `
precision highp float;

uniform float u_time;
uniform vec2 u_resolution;
uniform vec2 u_mouse;
uniform float u_zoom;
uniform float u_energy;
uniform float u_turbulence;
uniform float u_cohesion;
uniform float u_drift;
uniform vec4 u_palette[4];
uniform vec3 u_layers[3];
uniform vec2 u_driftDirection; // Seed-направление движения (генерируется при загрузке)
uniform vec2 u_dissipationCenter; // Центр рассеивания (от hover/клика)
uniform float u_dissipationStrength; // Сила рассеивания (0.0 = нет, 1.0 = максимальная)
uniform float u_lightIntensity; // Интенсивность света (градиент поля)

vec3 mod289(vec3 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 mod289(vec4 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 permute(vec4 x) {
  return mod289(((x*34.0)+1.0)*x);
}

vec4 taylorInvSqrt(vec4 r) {
  return 1.79284291400159 - 0.85373472095314 * r;
}

float snoise(vec3 v) {
  const vec2 C = vec2(1.0/6.0, 1.0/3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);

  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);

  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;

  i = mod289(i);
  vec4 p = permute(permute(permute(
    i.z + vec4(0.0, i1.z, i2.z, 1.0))
    + i.y + vec4(0.0, i1.y, i2.y, 1.0))
    + i.x + vec4(0.0, i1.x, i2.x, 1.0));

  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);

  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);

  vec4 s0 = floor(b0)*2.0 + 1.0;
  vec4 s1 = floor(b1)*2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;

  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);

  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;

  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}

float fbm(vec3 p) {
  float value = 0.0;
  float amplitude = 0.5;
  float frequency = 1.0;
  
  for (int i = 0; i < 4; i++) {
    value += amplitude * snoise(p * frequency);
    frequency *= 2.0;
    amplitude *= 0.5;
  }
  
  return value;
}

vec2 mouseDisturbance(vec2 uv, vec2 mouse) {
  vec2 delta = uv - mouse;
  float dist = length(delta);
  float influence = exp(-dist * 8.0) * 0.3;
  return delta * influence;
}

// Мягкое рассеивание тумана при hover/клике
// Рассеивает часть тумана, но не полностью открывает - оставляет легкую дымку
float dissipationEffect(vec2 uv, vec2 center, float strength) {
  if (strength < 0.01) return 1.0; // Нет рассеивания
  
  float dist = length(uv - center);
  // Большой радиус и очень мягкий falloff для плавного перехода
  float radius = 0.5; // Радиус влияния
  float falloff = 0.4; // Очень большая зона мягкого перехода
  
  // Плавная функция рассеивания с очень мягкими краями
  float fade = smoothstep(radius - falloff, radius + falloff, dist);
  
  // Частичное рассеивание - не полностью открываем, оставляем легкую дымку
  // Максимальное рассеивание - до 40% (остается 60% тумана даже в центре)
  float maxDissipation = 0.4;
  float effect = 1.0 - fade * strength * maxDissipation;
  
  // Гарантируем минимальную плотность для легкости
  effect = max(effect, 0.5);
  
  return effect;
}

  // Минимальное сферическое свечение (почти убрано для сохранения цветов)
  vec3 goldenLightRays(vec2 uv, float time) {
    // Центральный источник света (немного смещен вверх)
    vec2 lightCenter = vec2(0.5, 0.42);
    float distFromLight = length(uv - lightCenter);
    
    // Очень слабое свечение - минимальное влияние
    float centerGlow = exp(-distFromLight * 2.2) * 0.05; // Сильно уменьшено
    
    // Ограничиваем максимальную интенсивность
    float totalLight = min(centerGlow, 0.05);
    
    // Цвет свечения: почти белый
    vec3 lightColor = vec3(1.0, 1.0, 0.99);
    
    return lightColor * totalLight;
  }
  
  // Свет как градиент поля (не объект, а разлитый свет)
  vec3 fieldLightGradient(vec2 uv, float intensity, float time) {
    // Минимальный градиент - почти не влияет на цвета
    float distFromCenter = length(uv - 0.5);
    float lightGradient = 1.0 - smoothstep(0.0, 0.9, distFromCenter) * 0.02; // Очень слабый градиент
    
    // Усиление при взаимодействии - минимальное
    lightGradient *= (1.0 + intensity * 0.02);
    
    // Добавляем очень слабое свечение
    vec3 lightGlow = goldenLightRays(uv, time);
    lightGradient = min(lightGradient * 1.02, 1.0);
    
    // Комбинируем градиент со светом (очень минимальное влияние)
    return vec3(lightGradient) + lightGlow * 0.05;
  }

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution.xy;
  vec2 centered = (uv - 0.5) * u_zoom + 0.5;
  vec2 mouseDist = mouseDisturbance(centered, u_mouse);
  vec2 disturbed = centered + mouseDist * u_turbulence;
  
  // Естественное движение облаков с плавным покачиванием
  // Основное направление движения
  vec2 baseDirection = normalize(u_driftDirection);
  
  // Очень плавное покачивание для естественности (медленные колебания)
  // Используем очень медленные синусоидальные колебания для естественного движения
  float swayX = sin(u_time * 0.04) * 0.08; // Очень легкое покачивание по X
  float swayY = cos(u_time * 0.06) * 0.05; // Очень легкое покачивание по Y
  
  // Комбинируем основное направление с покачиванием
  vec2 naturalDirection = baseDirection + vec2(swayX, swayY);
  naturalDirection = normalize(naturalDirection); // Нормализуем для сохранения скорости
  
  // Плавное движение с естественным покачиванием
  // Используем плавную функцию времени для более естественного движения
  float smoothTime = u_time * (1.0 + sin(u_time * 0.03) * 0.05); // Очень легкие вариации скорости
  vec2 driftOffset = naturalDirection * smoothTime * u_drift;
  
  // Применяем рассеивание (мягкая зона с большим falloff)
  float dissipation = dissipationEffect(centered, u_dissipationCenter, u_dissipationStrength);
  
  vec3 color = vec3(0.0);
  float totalOpacity = 0.0;
  
  // Обрабатываем каждый слой отдельно (избегаем динамической индексации массивов)
  // Слой 0
  vec3 layer0 = u_layers[0];
  float scale0 = layer0.x;
  float speed0 = layer0.y;
  float opacity0 = layer0.z;
  // Добавляем индивидуальное покачивание для каждого слоя (разные частоты, очень плавно)
  float layerSway0 = sin(u_time * 0.03 + scale0 * 0.5) * 0.04;
  vec2 layerOffset0 = driftOffset * speed0 + vec2(layerSway0, cos(u_time * 0.04 + scale0 * 0.5) * 0.03);
  vec2 pos0_2d = disturbed * scale0 + layerOffset0;
  vec3 pos0 = vec3(pos0_2d.x, pos0_2d.y, u_time * speed0 * 0.5);
  float noise0 = fbm(pos0);
  // Плотность облаков - используем cohesion напрямую для лучшей видимости
  float density0 = smoothstep(-0.5, 0.5, noise0 * u_cohesion);
  float colorMix0 = (noise0 * 0.5 + 0.5) * 3.0;
  float t0 = fract(colorMix0);
  float colorIndex0 = floor(colorMix0);
  if (colorIndex0 < 0.0) colorIndex0 = 0.0;
  if (colorIndex0 > 2.0) colorIndex0 = 2.0;
  vec4 layerColor0;
  if (colorIndex0 < 1.0) {
    layerColor0 = mix(u_palette[0], u_palette[1], t0);
  } else if (colorIndex0 < 2.0) {
    layerColor0 = mix(u_palette[1], u_palette[2], t0);
  } else {
    layerColor0 = mix(u_palette[2], u_palette[3], t0);
  }
  color += layerColor0.rgb * density0 * opacity0;
  totalOpacity += density0 * opacity0;
  
  // Слой 1
  vec3 layer1 = u_layers[1];
  float scale1 = layer1.x;
  float speed1 = layer1.y;
  float opacity1 = layer1.z;
  // Индивидуальное покачивание для слоя 1 (очень плавно)
  float layerSway1 = sin(u_time * 0.035 + scale1 * 0.5) * 0.05;
  vec2 layerOffset1 = driftOffset * speed1 + vec2(layerSway1, cos(u_time * 0.045 + scale1 * 0.5) * 0.035);
  vec2 pos1_2d = disturbed * scale1 + layerOffset1;
  vec3 pos1 = vec3(pos1_2d.x, pos1_2d.y, u_time * speed1 * 0.5);
  float noise1 = fbm(pos1);
  // Плотность облаков
  float density1 = smoothstep(-0.5, 0.5, noise1 * u_cohesion);
  float colorMix1 = (noise1 * 0.5 + 0.5) * 3.0;
  float t1 = fract(colorMix1);
  float colorIndex1 = floor(colorMix1);
  if (colorIndex1 < 0.0) colorIndex1 = 0.0;
  if (colorIndex1 > 2.0) colorIndex1 = 2.0;
  vec4 layerColor1;
  if (colorIndex1 < 1.0) {
    layerColor1 = mix(u_palette[0], u_palette[1], t1);
  } else if (colorIndex1 < 2.0) {
    layerColor1 = mix(u_palette[1], u_palette[2], t1);
  } else {
    layerColor1 = mix(u_palette[2], u_palette[3], t1);
  }
  color += layerColor1.rgb * density1 * opacity1;
  totalOpacity += density1 * opacity1;
  
  // Слой 2
  vec3 layer2 = u_layers[2];
  float scale2 = layer2.x;
  float speed2 = layer2.y;
  float opacity2 = layer2.z;
  // Индивидуальное покачивание для слоя 2 (очень плавно)
  float layerSway2 = sin(u_time * 0.04 + scale2 * 0.5) * 0.06;
  vec2 layerOffset2 = driftOffset * speed2 + vec2(layerSway2, cos(u_time * 0.05 + scale2 * 0.5) * 0.04);
  vec2 pos2_2d = disturbed * scale2 + layerOffset2;
  vec3 pos2 = vec3(pos2_2d.x, pos2_2d.y, u_time * speed2 * 0.5);
  float noise2 = fbm(pos2);
  // Плотность облаков
  float density2 = smoothstep(-0.5, 0.5, noise2 * u_cohesion);
  float colorMix2 = (noise2 * 0.5 + 0.5) * 3.0;
  float t2 = fract(colorMix2);
  float colorIndex2 = floor(colorMix2);
  if (colorIndex2 < 0.0) colorIndex2 = 0.0;
  if (colorIndex2 > 2.0) colorIndex2 = 2.0;
  vec4 layerColor2;
  if (colorIndex2 < 1.0) {
    layerColor2 = mix(u_palette[0], u_palette[1], t2);
  } else if (colorIndex2 < 2.0) {
    layerColor2 = mix(u_palette[1], u_palette[2], t2);
  } else {
    layerColor2 = mix(u_palette[2], u_palette[3], t2);
  }
  color += layerColor2.rgb * density2 * opacity2;
  totalOpacity += density2 * opacity2;
  
  if (totalOpacity > 0.0) {
    color /= totalOpacity;
  }
  
  // Применяем рассеивание (туман истончается, но не полностью)
  totalOpacity *= dissipation;
  
  // Цвет становится светлее в зоне рассеивания (минимально)
  // color = mix(color, color * 1.05, (1.0 - dissipation) * 0.1); // ЗАКОММЕНТИРОВАНО
  
  // ЗАКОММЕНТИРОВАНО: Свет как градиент поля
  // vec3 lightGradient = fieldLightGradient(centered, u_lightIntensity, u_time);
  // color = color * mix(vec3(1.0), lightGradient, 0.1);
  
  // ЗАКОММЕНТИРОВАНО: Минимальное свечение
  // vec3 goldenGlow = goldenLightRays(centered, u_time);
  // float glowThroughClouds = 1.0 - totalOpacity * 0.7;
  // float glowIntensity = goldenGlow.r * glowThroughClouds * (1.0 + (1.0 - dissipation) * 0.2);
  // vec3 lightColor = vec3(1.0, 1.0, 1.0);
  // color += lightColor * glowIntensity * 0.01;
  
  // Мягкая энергия с легкостью
  color *= u_energy;
  
  // ЗАКОММЕНТИРОВАНО: Смешивание с белым и гамма-коррекция
  // color = pow(color, vec3(0.98)); // Гамма-коррекция
  // color = min(color * 1.05, vec3(1.0)); // Увеличение яркости
  
  gl_FragColor = vec4(color, 1.0);
}
`;

interface Field1CanvasProps {
  className?: string;
  params?: Field1Params; // Опциональные параметры извне
}

/**
 * Компонент Field1Canvas - WebGL рендеринг очень светлого поля
 */
export default function Field1Canvas({ className, params: externalParams }: Field1CanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glRef = useRef<WebGLRenderingContext | null>(null);
  const programRef = useRef<WebGLProgram | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const controllerRef = useRef<Field1Controller>(new Field1Controller());
  const paramsRef = useRef<Field1Params | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Фиксированное направление движения облаков (диагональ справа налево, слегка вниз)
  // Вектор нормализован для плавного движения по экрану за 45-55 секунд
  const driftDirectionRef = useRef<[number, number]>([
    -0.8,  // Движение влево (основное направление)
    0.15   // Легкое движение вниз для естественности
  ]);
  
  // Состояние рассеивания (от hover/клика)
  const [dissipationCenter, setDissipationCenter] = useState<[number, number]>([-1, -1]);
  const [dissipationStrength, setDissipationStrength] = useState(0.0);
  const [lightIntensity, setLightIntensity] = useState(0.0);
  
  // Синхронизация внешних параметров
  useEffect(() => {
    if (externalParams) {
      paramsRef.current = externalParams;
    }
  }, [externalParams]);

  // Инициализация WebGL
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) {
      const errorMsg = 'WebGL not supported in this browser';
      console.error(errorMsg);
      setError(errorMsg);
      return;
    }

    glRef.current = gl;
    console.log('Field1Canvas: WebGL context created successfully');

    // Используем внешние параметры или дефолтные
    paramsRef.current = externalParams || DEFAULT_FIELD1_PARAMS;
    console.log('Field1Canvas: Using params:', paramsRef.current);

    // Компиляция shader
    function compileShader(source: string, type: number): WebGLShader | null {
      if (!gl) return null;
      
      const shader = gl.createShader(type);
      if (!shader) return null;

      gl.shaderSource(shader, source);
      gl.compileShader(shader);

      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        const shaderType = type === gl.VERTEX_SHADER ? 'VERTEX' : 'FRAGMENT';
        const errorLog = gl.getShaderInfoLog(shader);
        console.error(`[${shaderType} SHADER] Compile error:`, errorLog);
        console.error(`[${shaderType} SHADER] Source:`, source);
        gl.deleteShader(shader);
        return null;
      }

      return shader;
    }

    // Создание программы
    if (!gl) return;
    
    const vertexShader = compileShader(vertexShaderSource, gl.VERTEX_SHADER);
    const fragmentShader = compileShader(fragmentShaderSource, gl.FRAGMENT_SHADER);

    if (!vertexShader || !fragmentShader) {
      const errorMsg = 'Failed to compile shaders';
      console.error(errorMsg);
      setError(errorMsg);
      return;
    }
    
    console.log('Shaders compiled successfully');

    if (!gl) return;
    
    const program = gl.createProgram();
    if (!program) {
      console.error('Failed to create program');
      return;
    }

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const errorMsg = `Program link error: ${gl.getProgramInfoLog(program)}`;
      console.error(errorMsg);
      setError(errorMsg);
      return;
    }

    programRef.current = program;
    console.log('WebGL program linked successfully');

    // Создание полноэкранного квада
    if (!gl) return;
    
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([
        -1, -1,
         1, -1,
        -1,  1,
        -1,  1,
         1, -1,
         1,  1,
      ]),
      gl.STATIC_DRAW
    );

    const positionLocation = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    // Обработка изменения размера
    function resize() {
      if (!canvas || !gl) return;
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const width = Math.max(1, Math.floor(rect.width * dpr));
      const height = Math.max(1, Math.floor(rect.height * dpr));
      
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
        console.log('Canvas resized:', width, 'x', height);
      }
    }

    // Первоначальная установка размера
    resize();
    window.addEventListener('resize', resize);
    
    // Убеждаемся, что canvas имеет размер
    if (canvas.width === 0 || canvas.height === 0) {
      console.warn('Canvas has zero size, forcing resize');
      // Используем requestAnimationFrame для гарантии, что DOM готов
      requestAnimationFrame(() => {
        resize();
        if (canvas.width === 0 || canvas.height === 0) {
          // Если всё ещё ноль, устанавливаем минимальный размер
          canvas.width = 800;
          canvas.height = 600;
          gl.viewport(0, 0, 800, 600);
          console.log('Forced canvas size to 800x600');
        }
      });
    }

    // Обработка мыши
    function handleMouseMove(e: MouseEvent) {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      controllerRef.current.setMouse(x, 1.0 - y); // Инвертируем Y для GL координат
      
      // Мягкое рассеивание при hover (не резкое, а истончение)
      setDissipationCenter([x, 1.0 - y]);
      setDissipationStrength(0.5); // Умеренная сила для частичного рассеивания
      setLightIntensity(0.3); // Легкое усиление света при взаимодействии
    }
    
    // Обработка ухода мыши
    function handleMouseLeave() {
      // Плавно убираем рассеивание (easing → long → almost imperceptible)
      // Плавность обеспечивается рендер-циклом, который обновляется каждый кадр
      setDissipationStrength(0.0);
      setLightIntensity(0.0);
    }

    // Обработка скролла
    function handleWheel(e: WheelEvent) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const currentZoom = controllerRef.current.getState().zoom;
      controllerRef.current.setZoom(currentZoom + delta);
    }

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    canvas.addEventListener('wheel', handleWheel, { passive: false });

    // Анимационный цикл
    function render() {
      if (!gl || !program || !canvas) {
        console.error('WebGL context, program or canvas not available');
        return;
      }

      // Используем внешние параметры (если есть), иначе загруженные или дефолтные
      // ВАЖНО: Проверяем externalParams каждый кадр для реального времени
      const baseParams = externalParams || paramsRef.current || DEFAULT_FIELD1_PARAMS;
      if (externalParams) {
        paramsRef.current = externalParams; // Синхронизируем ref
      }

      // Обновляем контроллер
      controllerRef.current.update();
      const controllerState = controllerRef.current.getState();

      // Вычисляем время
      const time = (Date.now() - startTimeRef.current) / 1000.0;

      // Используем параметры напрямую
      const modifiedParams: Field1Params = baseParams;

      // Преобразуем параметры в uniforms
      let uniforms;
      try {
        uniforms = paramsToUniforms(
          modifiedParams,
          time,
          [canvas.width, canvas.height],
          controllerState.mouse,
          controllerState.zoom
        );
      } catch (err) {
        console.error('Error converting params to uniforms:', err);
        animationFrameRef.current = requestAnimationFrame(render);
        return;
      }

      // Устанавливаем uniforms
      gl.useProgram(program);

      const timeLoc = gl.getUniformLocation(program, 'u_time');
      const resolutionLoc = gl.getUniformLocation(program, 'u_resolution');
      const mouseLoc = gl.getUniformLocation(program, 'u_mouse');
      const zoomLoc = gl.getUniformLocation(program, 'u_zoom');
      const energyLoc = gl.getUniformLocation(program, 'u_energy');
      const turbulenceLoc = gl.getUniformLocation(program, 'u_turbulence');
      const cohesionLoc = gl.getUniformLocation(program, 'u_cohesion');
      const driftLoc = gl.getUniformLocation(program, 'u_drift');
      const driftDirectionLoc = gl.getUniformLocation(program, 'u_driftDirection');
      const dissipationCenterLoc = gl.getUniformLocation(program, 'u_dissipationCenter');
      const dissipationStrengthLoc = gl.getUniformLocation(program, 'u_dissipationStrength');
      const lightIntensityLoc = gl.getUniformLocation(program, 'u_lightIntensity');

      gl.uniform1f(timeLoc, uniforms.u_time);
      gl.uniform2f(resolutionLoc, uniforms.u_resolution[0], uniforms.u_resolution[1]);
      gl.uniform2f(mouseLoc, uniforms.u_mouse[0], uniforms.u_mouse[1]);
      gl.uniform1f(zoomLoc, uniforms.u_zoom);
      gl.uniform1f(energyLoc, uniforms.u_energy);
      gl.uniform1f(turbulenceLoc, uniforms.u_turbulence);
      gl.uniform1f(cohesionLoc, uniforms.u_cohesion);
      gl.uniform1f(driftLoc, uniforms.u_drift);
      
      // Новые uniforms для seed-направления и рассеивания
      gl.uniform2f(driftDirectionLoc, driftDirectionRef.current[0], driftDirectionRef.current[1]);
      gl.uniform2f(dissipationCenterLoc, dissipationCenter[0], dissipationCenter[1]);
      gl.uniform1f(dissipationStrengthLoc, dissipationStrength);
      gl.uniform1f(lightIntensityLoc, lightIntensity);

      // Устанавливаем массив палитры (4 цвета)
      // В WebGL массивы uniforms устанавливаются по элементам
      for (let i = 0; i < 4; i++) {
        const loc = gl.getUniformLocation(program, `u_palette[${i}]`);
        if (loc) {
          gl.uniform4f(
            loc,
            uniforms.u_palette[i * 4 + 0],
            uniforms.u_palette[i * 4 + 1],
            uniforms.u_palette[i * 4 + 2],
            uniforms.u_palette[i * 4 + 3]
          );
        }
      }

      // Устанавливаем массив слоёв (3 слоя)
      for (let i = 0; i < 3; i++) {
        const loc = gl.getUniformLocation(program, `u_layers[${i}]`);
        if (loc) {
          gl.uniform3f(
            loc,
            uniforms.u_layers[i * 3 + 0],
            uniforms.u_layers[i * 3 + 1],
            uniforms.u_layers[i * 3 + 2]
          );
        }
      }

      // Проверяем ошибки WebGL
      const glError = gl.getError();
      if (glError !== gl.NO_ERROR) {
        console.warn('WebGL error:', glError);
      }

      // Рендерим
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      animationFrameRef.current = requestAnimationFrame(render);
    }

    // Запускаем рендер
    render();
    
    // Устанавливаем флаг инициализации после первого кадра
    setTimeout(() => {
      setIsInitialized(true);
    }, 200);

    // Очистка
    return () => {
      window.removeEventListener('resize', resize);
      if (canvas) {
        canvas.removeEventListener('mousemove', handleMouseMove);
        canvas.removeEventListener('mouseleave', handleMouseLeave);
        canvas.removeEventListener('wheel', handleWheel);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Fallback для случаев ошибки или отсутствия WebGL
  if (error) {
    return (
      <div
        className={className}
        style={{
          width: '100%',
          height: '100%',
          background: 'radial-gradient(ellipse at center, hsl(220, 50%, 90%) 0%, hsl(240, 45%, 85%) 50%, hsl(260, 40%, 80%) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '14px',
        }}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div>⚠️ {error}</div>
          <div style={{ marginTop: '10px', fontSize: '12px', opacity: 0.7 }}>
            Используется упрощённый режим
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <canvas
        ref={canvasRef}
        className={className}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          cursor: 'default',
          backgroundColor: isInitialized ? 'transparent' : 'hsl(220, 50%, 90%)',
        }}
      />
      {!isInitialized && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'rgba(255, 255, 255, 0.4)',
            fontSize: '12px',
          }}
        >
          Инициализация поля...
        </div>
      )}
    </>
  );
}

